import React, { useMemo } from "react";
import { Group, NumberInput, Select, Stack, Text } from "@mantine/core";
import { SalonService } from "../../../types/context";
import { BogoPercentTemplateParams } from "../../../types/packages";
import { formatCurrency } from "../../../utils/cashDrawerUtils";

/**
 * Builder state for the "Buy X, Get Y % Off" offer. Number fields hold
 * `number | string` because Mantine NumberInput emits "" while typing.
 */
export interface BogoBuilderState {
  buyServiceId: string | null;
  buyQty: number | string;
  getServiceId: string | null;
  getQty: number | string;
  percentOff: number | string;
}

export function defaultBogoState(): BogoBuilderState {
  return {
    buyServiceId: null,
    buyQty: 1,
    getServiceId: null,
    getQty: 1,
    percentOff: 100,
  };
}

export function bogoStateFromParams(
  params: BogoPercentTemplateParams,
): BogoBuilderState {
  return {
    buyServiceId: params.buy_service_id,
    buyQty: params.buy_qty,
    getServiceId: params.get_service_id,
    getQty: params.get_qty,
    percentOff: params.percent_off,
  };
}

const num = (value: number | string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/** Backend-shaped params, or null while the builder is incomplete. */
export function bogoStateToParams(
  state: BogoBuilderState,
): BogoPercentTemplateParams | null {
  const buyQty = num(state.buyQty);
  const getQty = num(state.getQty);
  const percentOff = num(state.percentOff);
  if (
    !state.buyServiceId ||
    !state.getServiceId ||
    !(buyQty >= 0.01) ||
    !(getQty >= 0.01) ||
    !(percentOff > 0 && percentOff <= 100)
  ) {
    return null;
  }
  return {
    buy_service_id: state.buyServiceId,
    buy_qty: buyQty,
    get_service_id: state.getServiceId,
    get_qty: getQty,
    percent_off: percentOff,
  };
}

interface BogoBuilderProps {
  services: SalonService[];
  value: BogoBuilderState;
  onChange: (next: BogoBuilderState) => void;
}

/**
 * Inline sentence builder: "Buy [n] × [service], get [m] × [service],
 * for [p]% off". 100% off means the "get" units are free.
 */
export default function BogoBuilder({
  services,
  value,
  onChange,
}: BogoBuilderProps) {
  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        value: service.id,
        label: `${service.name} (${formatCurrency(service.default_price ?? 0)})`,
      })),
    [services],
  );

  const update = (updates: Partial<BogoBuilderState>) =>
    onChange({ ...value, ...updates });

  return (
    <Stack gap="sm">
      {/* Buy line */}
      <Group wrap="wrap" align="center" gap="xs">
        <Text size="sm" w={32}>
          Buy
        </Text>
        <NumberInput
          aria-label="Buy quantity"
          value={value.buyQty}
          onChange={(next) => update({ buyQty: next })}
          min={0.01}
          decimalScale={2}
          hideControls
          allowNegative={false}
          w={70}
        />
        <Text size="sm">×</Text>
        <Select
          aria-label="Buy service"
          placeholder="Select a service"
          data={serviceOptions}
          value={value.buyServiceId}
          onChange={(next) => update({ buyServiceId: next })}
          searchable
          w={240}
        />
      </Group>

      {/* Get line */}
      <Group wrap="wrap" align="center" gap="xs">
        <Text size="sm" w={32}>
          Get
        </Text>
        <NumberInput
          aria-label="Get quantity"
          value={value.getQty}
          onChange={(next) => update({ getQty: next })}
          min={0.01}
          decimalScale={2}
          hideControls
          allowNegative={false}
          w={70}
        />
        <Text size="sm">×</Text>
        <Select
          aria-label="Get service"
          placeholder="Select a service"
          data={serviceOptions}
          value={value.getServiceId}
          onChange={(next) => update({ getServiceId: next })}
          searchable
          w={240}
        />
        <Text size="sm">for</Text>
        <NumberInput
          aria-label="Percent off"
          value={value.percentOff}
          onChange={(next) => update({ percentOff: next })}
          min={1}
          max={100}
          decimalScale={2}
          hideControls
          allowNegative={false}
          w={90}
        />
        <Text size="sm">% off</Text>
      </Group>
    </Stack>
  );
}
